import { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import { IoCloseOutline } from "react-icons/io5"

import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import moment from "moment"

import Calendar from "../components/calendar/Calendar"
import TimePicker from "../components/timePicker/TimePicker"
// import TimePicker from "../timePicker/TimePicker"
import "./dateTime-customV2.css"

moment.updateLocale("en", {
  weekdaysShort: ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"],
})

// // Minimum date - tomorrow
// const tomorrow = moment().add(1, "day").startOf("day")

// Compute when "today" is still valid:
// nextHour = now  1h
// if nextHour is still today, allow selecting today; otherwise minDate is nextHour's day.
const nextHour = moment().add(1, "hour")
const minDate = nextHour.isSame(moment(), "day")
  ? moment().startOf("day")
  : nextHour.startOf("day")

export interface DateTimePickerModalHandle {
  openModal: () => void
  closeModal: () => void
}

type DateTimeState = {
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  activeField: "startDate" | "startTime" | "endDate" | "endTime"
}
interface DateTimePickerModalProps {
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  onConfirm: (state: DateTimeState, isVisible: boolean) => void
  setIsDatePickerModalOpen: (state: boolean) => void
  isDefaultBg?: boolean
  initialActiveField?: "startDate" | "startTime" | "endDate" | "endTime"
  showStartOnly?: boolean
  showEndOnly?: boolean
}

const DateTimePickerModal = forwardRef<
  DateTimePickerModalHandle,
  DateTimePickerModalProps
>(
  (
    {
      startDate,
      startTime,
      endDate,
      endTime,
      onConfirm,
      setIsDatePickerModalOpen,
      isDefaultBg = false,
      initialActiveField = "startDate",
      showStartOnly = false,
      showEndOnly = false,
    },
    ref,
  ) => {
    const [isVisible, setIsVisible] = useState(false)
    const [state, setState] = useState<DateTimeState>({
      startDate: startDate,
      startTime: startTime,
      endDate: endDate,
      endTime: endTime,
      activeField: initialActiveField,
    })

    useEffect(() => {
      // Update active field whenever showStartOnly/showEndOnly changes
      if (showEndOnly) {
        setState((prev) => ({ ...prev, activeField: "endDate" }))
      } else if (showStartOnly) {
        setState((prev) => ({ ...prev, activeField: "startDate" }))
      }
    }, [showEndOnly, showStartOnly])

    const resetModal = () => {
      setIsDatePickerModalOpen(false)
      setIsVisible(false)
    }

    //update parent component field values
    useEffect(() => {
      onConfirm(state, isVisible)
    }, [state, isVisible])

    // Helper function to update state
    const updateState = (updates: Partial<DateTimeState>) => {
      setState((prev) => ({ ...prev, ...updates }))
    }

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      openModal: () => {
        setIsVisible(true)
        // Always ensure we're showing the calendar when opening
        setState((prev) => ({
          ...prev,
          startDate,
          startTime,
          endTime,
          endDate, // added so that, when the modal opens we get the updated values
          activeField: showEndOnly ? "endDate" : "startDate",
        }))
      },
      closeModal: resetModal,
    }))

    // Helper function to format date for display
    const formatDateForDisplay = (date: moment.Moment) => {
      return date.format("MMM D, YYYY")
    }

    // Handle calendar date selection
    const handleDateChange = (date: moment.Moment) => {
      if (state.activeField === "startDate") {
        updateState({
          startDate: date.format("MMMM DD YYYY"),
        })
      } else if (state.activeField === "endDate") {
        updateState({ endDate: date.format("MMMM DD YYYY") })
      }
    }

    // // Handle time selection
    const handleTimeChange = (time: string) => {
      if (state.activeField === "startTime") {
        updateState({ startTime: time })
      } else if (state.activeField === "endTime") {
        updateState({ endTime: time })
      }
    }

    if (!isVisible) return null

    // Determine what to render based on active field
    const renderContent = () => {
      if (
        state.activeField === "startDate" ||
        state.activeField === "endDate"
      ) {
        return (
          <Calendar
            selectedDate={
              state.activeField === "startDate"
                ? moment(state.startDate)
                : moment(state.endDate)
            }
            onChange={handleDateChange}
            minDate={
              state.activeField === "startDate"
                ? minDate
                : moment(state.startDate)
            }
            dateTimeState={state}
          />
        )
      } else {
        return (
          <TimePicker
            selectedTime={
              state.activeField === "startTime"
                ? state.startTime
                : state.endTime
            }
            onChange={handleTimeChange}
            dateTimeState={state}
          />
        )
      }
    }

    // Determine if we're selecting a start field or end field
    const isStartFieldActive =
      state.activeField === "startDate" || state.activeField === "startTime"

    return (
      <div
        className="fixed inset-0 flex justify-center bg-black bg-opacity-50 z-[100] items-end md:items-center place-items-center"
        onClick={resetModal}
      >
        <BlurWrapper
          overlayOpacityDefault="20"
          className={`w-full md:max-w-sm ${
            isDefaultBg ? "!bg-black/50" : " !bg-gray-500/10"
          } rounded-t-2xl md:rounded-2xl relative flex flex-col cursor-auto place-content-center p-6 gap-6`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-center relative">
            <h2 className="text-white text-lg font-medium text-center">
              Date and time
            </h2>
            <div className="absolute right-0" onClick={resetModal}>
              <IoCloseOutline color="#fff" size={20} />
            </div>
            {/* <button
            className="text-[#0A84FF] font-semibold"
            onClick={handleConfirm}
          >
            Done
          </button> */}
          </div>

          {/* Selector tabs */}
          <div className="">
            {/* Start Date/Time Row */}
            {!showEndOnly && (
              <div className="mb-4 flex flex-row items-center border-t border-b py-3 border-white/10">
                <div className="text-white/70 text-base w-[20%] text-left">
                  Starts
                </div>
                <div className="flex gap-2">
                  <button
                    className={`py-2 px-3 rounded-md bg-gray-400/10 ${state.activeField === "startDate" ? "text-[#0a84ff]" : " text-white"}`}
                    onClick={() => updateState({ activeField: "startDate" })}
                  >
                    {formatDateForDisplay(moment(state.startDate))}
                  </button>
                  <button
                    className={`py-2 px-3 rounded-md bg-gray-400/10 ${state.activeField === "startTime" ? "text-[#0a84ff]" : " text-white"}`}
                    onClick={() => updateState({ activeField: "startTime" })}
                  >
                    {state.startTime}
                  </button>
                </div>
              </div>
            )}

            {/* Render Calendar or TimePicker content between start and end if startField is active */}
            {isStartFieldActive && !showEndOnly && (
              <div className="py-4 h-96">{renderContent()}</div>
            )}

            {/* End Date/Time Row */}
            {!showStartOnly && (
              <div
                className={`mb-4 flex flex-row items-center pb-3 ${
                  state.activeField === "endDate" ||
                  state.activeField === "endTime"
                    ? "border-b border-white/10"
                    : "border-t border-white/10 pt-3"
                }`}
              >
                <div className="text-white/70 text-base w-[20%] text-left">
                  Ends
                </div>
                <div className="flex gap-2">
                  <button
                    className={`py-2 px-3 rounded-md bg-gray-400/10 ${state.activeField === "endDate" ? "text-[#0a84ff]" : " text-white"}`}
                    onClick={() => updateState({ activeField: "endDate" })}
                  >
                    {formatDateForDisplay(moment(state.endDate))}
                  </button>
                  <button
                    className={`py-2 px-3 rounded-md bg-gray-400/10 ${state.activeField === "endTime" ? "text-[#0a84ff]" : " text-white"}`}
                    onClick={() => updateState({ activeField: "endTime" })}
                  >
                    {state.endTime}
                  </button>
                </div>
              </div>
            )}

            {/* Render Calendar or TimePicker content below end section if endField is active */}
            {!isStartFieldActive && !showStartOnly && (
              <div className="py-4 h-96">{renderContent()}</div>
            )}
          </div>
        </BlurWrapper>
      </div>
    )
  },
)

export default DateTimePickerModal
